import { useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import {
  REFERRAL_EVENT_KIND_LABELS,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUSES_BY_DIRECTION,
  REFERRAL_URGENCY_LABELS,
  REFERRAL_URGENCIES,
  type ReferralDirection,
} from '@wodi/domain';
import type { ReferralRowView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  downloadReferrals,
  fetchReferralPage,
  referralKeys,
  type ReferralListFilters,
} from '../api/referrals.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { toasts } from '../toasts.ts';
import { referralStatusTag } from './labels.ts';
import styles from './referrals.module.css';

// The facility's referrals (docs/plans/phase2-referrals.md §1.1 item 6, §6): Sent and
// Received, filtered by status, urgency, facility and referral dates, newest first. A
// referral of a visit the reader isn't cleared for is never listed. The CSV lists patients,
// so it needs its own permission and is audited.

const route = getRouteApi('/_app/referrals');

const ANY = 'any';

interface Filters {
  status: string;
  urgency: string;
  facility: string;
  from: string | null;
  to: string | null;
}

const NO_FILTERS: Filters = { status: ANY, urgency: ANY, facility: '', from: null, to: null };

function asQuery(direction: ReferralDirection, f: Filters): ReferralListFilters {
  const facility = f.facility.trim();
  // A status of the other list (after switching lists) is left out, not sent.
  const status = REFERRAL_STATUSES_BY_DIRECTION[direction].find((s) => s === f.status);
  return {
    direction,
    ...(status === undefined ? {} : { status }),
    ...(f.urgency === ANY ? {} : { urgency: f.urgency }),
    ...(facility.length >= 2 ? { facility } : {}),
    ...(f.from === null ? {} : { from: f.from }),
    ...(f.to === null ? {} : { to: f.to }),
  };
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function ReferralTable({
  direction,
  items,
  empty,
}: {
  direction: ReferralDirection;
  items: readonly ReferralRowView[];
  empty: string;
}) {
  return (
    <TableScroll>
      <Table flat aria-label={direction === 'outgoing' ? 'Referrals sent' : 'Referrals received'}>
        <TableHeader>
          <Column isRowHeader>{direction === 'outgoing' ? 'Letter' : 'Their reference'}</Column>
          <Column>Patient</Column>
          <Column>{direction === 'outgoing' ? 'To' : 'From'}</Column>
          <Column>Urgency</Column>
          <Column>Referred</Column>
          <Column>Status</Column>
          <Column>Last update</Column>
        </TableHeader>
        <TableBody items={items} renderEmptyState={() => empty}>
          {(r) => {
            const tag = referralStatusTag(r.status);
            return (
              <Row id={r.id}>
                <Cell>
                  <Link to="/referrals/$referralId" params={{ referralId: r.id }}>
                    {r.number === null ? (
                      'No reference'
                    ) : (
                      <span className="wodi-id">{r.number}</span>
                    )}
                  </Link>
                </Cell>
                <Cell>
                  {`${r.patient.name} `}
                  <span className="wodi-id">{r.patient.patientNumber}</span>
                </Cell>
                <Cell>
                  {r.otherFacility.name}
                  {r.otherFacility.fromRegistry ? null : (
                    <span className={styles.meta}> Typed; not checked</span>
                  )}
                </Cell>
                <Cell>{REFERRAL_URGENCY_LABELS[r.urgency]}</Cell>
                <Cell>{formatInstant(r.referredAt)}</Cell>
                <Cell>
                  <Tag state={tag.state}>{tag.word}</Tag>
                </Cell>
                <Cell>
                  {REFERRAL_EVENT_KIND_LABELS[r.lastEventKind]}
                  <span className={styles.meta}> {formatInstant(r.lastEventAt)}</span>
                </Cell>
              </Row>
            );
          }}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

function List({ direction, filters }: { direction: ReferralDirection; filters: Filters }) {
  // Typing a facility name waits for a pause before it asks again.
  const facility = useDebounced(filters.facility, 300);
  const query = asQuery(direction, { ...filters, facility });
  const pages = useInfiniteQuery({
    queryKey: referralKeys.list(query),
    queryFn: ({ pageParam }) => fetchReferralPage(query, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  if (pages.isPending) return <LoadingBlock label="Loading referrals" rows={5} />;
  if (pages.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void pages.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(pages.error, "Referrals couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const items = pages.data.pages.flatMap((p) => p.items);
  const filtered = JSON.stringify(filters) !== JSON.stringify(NO_FILTERS);
  const empty = filtered
    ? 'No referrals match these filters. Clear the filters to see them all.'
    : direction === 'outgoing'
      ? 'No referrals sent yet. Referral letters written in a consultation or ward round appear here.'
      : "No referrals received yet. Reception logs them from the patient's record when the patient arrives.";
  return (
    <>
      <ReferralTable direction={direction} items={items} empty={empty} />
      {pages.hasNextPage ? (
        <div className={styles.more}>
          <Button
            isPending={pages.isFetchingNextPage}
            pendingLabel="Loading…"
            onPress={() => void pages.fetchNextPage()}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </>
  );
}

function FilterBar({
  direction,
  filters,
  onChange,
}: {
  direction: ReferralDirection;
  filters: Filters;
  onChange: (next: Filters) => void;
}) {
  const statuses = REFERRAL_STATUSES_BY_DIRECTION[direction];
  return (
    <div className={styles.filters} role="group" aria-label="Filters">
      <Select
        label="Status"
        dense
        options={[
          { id: ANY, label: 'Any status' },
          ...statuses.map((s) => ({ id: s, label: REFERRAL_STATUS_LABELS[s] })),
        ]}
        value={statuses.some((s) => s === filters.status) ? filters.status : ANY}
        onChange={(v) => {
          onChange({ ...filters, status: String(v) });
        }}
      />
      <Select
        label="Urgency"
        dense
        options={[
          { id: ANY, label: 'Any urgency' },
          ...REFERRAL_URGENCIES.map((u) => ({ id: u, label: REFERRAL_URGENCY_LABELS[u] })),
        ]}
        value={filters.urgency}
        onChange={(v) => {
          onChange({ ...filters, urgency: String(v) });
        }}
      />
      <TextField
        label="Facility"
        description="Part of the name or MFL code"
        value={filters.facility}
        onChange={(facility) => {
          onChange({ ...filters, facility });
        }}
      />
      <DateField
        label="Referred from"
        value={filters.from}
        onChange={(from) => {
          onChange({ ...filters, from });
        }}
      />
      <DateField
        label="Referred to"
        value={filters.to}
        onChange={(to) => {
          onChange({ ...filters, to });
        }}
      />
      <Button
        variant="quiet"
        isDisabled={JSON.stringify(filters) === JSON.stringify(NO_FILTERS)}
        onPress={() => {
          onChange(NO_FILTERS);
        }}
      >
        Clear filters
      </Button>
    </div>
  );
}

export function ReferralsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { direction: requested } = route.useSearch();
  const direction: ReferralDirection = requested ?? 'outgoing';
  const navigate = useNavigate();
  const online = useOnline();
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const goTo = (next: ReferralDirection) => () => {
    void navigate({ to: '/referrals', search: { direction: next } });
  };
  useShortcut('1', goTo('outgoing'), { description: 'Referrals sent' });
  useShortcut('2', goTo('incoming'), { description: 'Referrals received' });

  if (session === null || !can(session, 'referrals.read')) {
    return (
      <>
        <PageHeader title="Referrals" />
        <Notice tone="info">
          You don't have permission to see referrals. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  const mayExport = can(session, 'referrals.export');
  return (
    <div className={styles.page}>
      <PageHeader
        title="Referrals"
        description="Patients referred to other facilities, and patients referred here."
        actions={
          mayExport ? (
            <Button
              isDisabled={!online}
              isPending={downloading}
              pendingLabel="Downloading…"
              onPress={() => {
                setDownloading(true);
                setDownloadError(null);
                downloadReferrals(asQuery(direction, filters))
                  .then(() => {
                    toasts.show({ title: 'Referrals downloaded' });
                  })
                  .catch((error: unknown) => {
                    setDownloadError(errorText(error, "The list couldn't be downloaded."));
                  })
                  .finally(() => {
                    setDownloading(false);
                  });
              }}
            >
              Download CSV
            </Button>
          ) : null
        }
      />
      <div className={styles.toolbar}>
        <nav aria-label="Referrals" className={screen.tabs}>
          {(
            [
              ['outgoing', 'Sent', '1'],
              ['incoming', 'Received', '2'],
            ] as const
          ).map(([d, label, key]) => (
            <Link
              key={d}
              to="/referrals"
              search={{ direction: d }}
              aria-keyshortcuts={key}
              {...(d === direction ? { 'aria-current': 'page' as const } : {})}
            >
              {label}
            </Link>
          ))}
        </nav>
        <ShortcutHint combo="1" to="2" action="switch between sent and received" />
      </div>
      {mayExport ? (
        <p className={styles.meta}>
          The CSV lists patients by name. Keep it on this computer and delete it when you are done.
        </p>
      ) : null}
      {downloadError === null ? null : <Notice tone="error">{downloadError}</Notice>}
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. The list shows what was last loaded; updates wait for the
          connection.
        </Notice>
      ) : null}
      <Section title={direction === 'outgoing' ? 'Referrals sent' : 'Referrals received'}>
        <div className={styles.fields}>
          <FilterBar
            direction={direction}
            filters={filters}
            onChange={(next) => {
              setFilters(next);
            }}
          />
          <List direction={direction} filters={filters} />
        </div>
      </Section>
    </div>
  );
}
